"use client";

import { usePathname } from "next/navigation";

import { useExperience } from "@/shared/experience";

/**
 * Adapted from the reference's SiteFooter (src/components/SiteFooter.tsx).
 *
 * The footer carries the APPLICATION's own identity, so it keys off the
 * SERVED SURFACE — deliberately the opposite input from the work-item
 * nouns, which key off the customer's org profile (see projectNoun()).
 * The two are not interchangeable and this file is the clearest case of
 * the difference: "which app am I on" decides whose copyright line this
 * is, while "is my company branded" decides whether my work is called an
 * Order or a Project.
 *
 * On the VIP surface this renders VIP's own footer verbatim — PSCX.ai and
 * the #860 Terms & Conditions URL. Those are the REAL, live PSCX links the
 * reference ships, not invented ZW2 equivalents; dropping them (as this
 * component briefly did) showed VIP users a ZoomWarriors copyright on the
 * login screen, which is precisely the tell the consolidation may not
 * produce. Corrected 2026-08-18 after diffing the two running apps.
 *
 * One deliberate structural change from the reference is kept: the
 * sidebar-route prefixes are a prop rather than hardcoded, so shared/ui
 * never imports the app routing layer.
 */

const PSCX_HOME_URL = "https://pscx.ai/";
// #860: the real Terms & Conditions page (the old "/#terms" anchor just
// landed on the marketing homepage).
const PSCX_TERMS_URL = "https://pscx.ai/terms_conditions/";

const DEFAULT_SIDEBAR_PREFIXES = ["/dashboard", "/scope", "/deals", "/admin"] as const;

interface SiteFooterProps {
  pathname?: string;
  sidebarRoutePrefixes?: readonly string[];
}

export function SiteFooter({
  pathname: pathnameProp,
  sidebarRoutePrefixes = DEFAULT_SIDEBAR_PREFIXES,
}: SiteFooterProps = {}) {
  const routerPathname = usePathname();
  const pathname = pathnameProp ?? routerPathname ?? "/";
  const hasSidebar = sidebarRoutePrefixes.some((prefix) => pathname.startsWith(prefix));

  const { status, surface } = useExperience();
  // Claim a brand ONLY once the experience has actually resolved. Until
  // then we know neither, so we claim neither.
  //
  // The first version of this fix rendered the ZoomWarriors line whenever
  // the surface was not vip and called it "neutral". It is not neutral —
  // it is the other brand's copyright, and it showed on the VIP login
  // screen for the whole bootstrap round-trip (D39 finding 4). Worse in
  // the `unavailable` state, which by construction only happens on a
  // MAPPED brand host whose profile failed to resolve: precisely a VIP
  // visitor, permanently shown a ZoomWarriors footer.
  //
  // The placeholder keeps the footer's height so the page does not shift
  // when the brand lands.
  const resolved = status === "ready";
  const isVip = resolved && surface === "vip";

  return (
    <footer
      aria-label="Site footer"
      className={`relative z-10 bg-transparent px-6 py-5 text-xs text-ink-faint ${hasSidebar ? "md:ml-64" : ""}`}
    >
      <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-center gap-2 text-center sm:flex-row sm:gap-3">
        {!resolved ? (
          <span aria-hidden="true" className="invisible">
            © 2026 All rights reserved.
          </span>
        ) : isVip ? (
          <>
            <span>© 2026</span>
            <a
              href={PSCX_HOME_URL}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-ink-light transition-colors hover:text-accent"
            >
              PSCX.ai
            </a>
            <span className="hidden text-subtle sm:inline">-</span>
            <span>All rights reserved.</span>
            <span className="hidden text-subtle sm:inline">|</span>
            <a
              href={PSCX_TERMS_URL}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-ink-light transition-colors hover:text-accent"
            >
              Terms &amp; Conditions
            </a>
          </>
        ) : (
          <span>© 2026 ZoomWarriors. All rights reserved.</span>
        )}
      </div>
    </footer>
  );
}
